import { useState } from "react";
import { KeyRound } from "lucide-react";
import { AppTopbar } from "../../../shell/AppTopbar";
import {
  Page,
  Button,
  Badge,
  Alert,
  Card,
  ChangePasswordModal,
  DescriptionList,
  ResponsiveGrid,
  Text,
  DateTimeField,
} from "@marekl/adminer/components";
import { notifySuccess } from "@marekl/adminer/api";
import { useAuth } from "../../../auth/useAuth";
import type { Assignment } from "../../../auth/queries";
import { ROLE_LABEL, SCOPE_LABEL } from "../../../lib/i18n/roles";
import styles from "./ProfilePage.module.css";

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function assignmentLabel(a: Assignment): string {
  const role = ROLE_LABEL[a.role] ?? a.role;
  if (a.scope_type === "global") return role;
  if (a.scope_name) return `${role} · ${a.scope_name}`;
  return `${role} (${SCOPE_LABEL[a.scope_type]})`;
}

export function ProfilePage() {
  const { user } = useAuth();
  const [pwOpen, setPwOpen] = useState(false);

  if (!user) return null;
  const name = user.display_name || user.email;

  return (
    <>
      <AppTopbar crumbs={[{ label: "Můj profil" }]} />
      <Page>
        <Page.Header title="Můj profil" subtitle="nastavení účtu a hesla" />
        <Page.Content>
          <ResponsiveGrid gap="lg">
            <Card>
              <Card.Header title="Účet" />
              <Card.Content>
                <div className={styles.identity}>
                  <div className={styles.avatar} aria-hidden="true">{initials(name)}</div>
                  <div className={styles.identityText}>
                    <div className={styles.name}>{name}</div>
                    <Text variant={["mono", "muted"]}>{user.email}</Text>
                  </div>
                </div>

                <DescriptionList labelWidth={120}>
                  <DescriptionList.Item label="Role">
                    {user.assignments.length === 0 ? (
                      <Text variant="muted">Žádné role</Text>
                    ) : (
                      <div className={styles.roleChips}>
                        {user.assignments.map((a, i) => (
                          <Badge key={i} variant="info">{assignmentLabel(a)}</Badge>
                        ))}
                      </div>
                    )}
                  </DescriptionList.Item>
                  <DescriptionList.Item label="Stav">
                      {user.active
                        ? <Badge variant="ok">Aktivní</Badge>
                        : <Badge variant="muted">Deaktivován</Badge>}
                  </DescriptionList.Item>
                  <DescriptionList.Item label="Posl. přihlášení"><DateTimeField record={user} source="last_login_at" emptyText={<Text variant="muted">—</Text>} /></DescriptionList.Item>
                </DescriptionList>
              </Card.Content>
            </Card>

            <Card>
              <Card.Header title="Heslo" />
              <Card.Content>
                {user.password_locked ? (
                  <Alert variant="info" title="Změna hesla je zakázána">
                    Heslo tohoto účtu spravuje administrátor; sami ho změnit nemůžete.
                  </Alert>
                ) : (
                  <div className={styles.pwBlock}>
                    <p className={styles.pwHint}>
                      Z bezpečnostních důvodů heslo pravidelně měňte a nesdílejte ho s ostatními.
                    </p>
                    <Button variant="primary" onClick={() => setPwOpen(true)}>
                      <KeyRound size={16} aria-hidden="true" />
                      Změnit heslo
                    </Button>
                  </div>
                )}
              </Card.Content>
            </Card>
          </ResponsiveGrid>
        </Page.Content>
      </Page>

      <ChangePasswordModal
        open={pwOpen}
        onClose={() => setPwOpen(false)}
        onSuccess={() => {
          setPwOpen(false);
          notifySuccess("Heslo bylo úspěšně změněno.");
        }}
      />
    </>
  );
}
